import { FileText, Package, Link2, Wrench, User, Shield } from "lucide-react";


// Define constants
export const SPDX = "SPDX";
export const CYCLONEDX = "CYCLONEDX";
export const ClaritySC = "ClaritySC";
export const Excel = "Excel";

export const SYSTEM_DEFAULT_SBOM_NAME = "System Default SBOM";

export const SBOM_TYPE_SPDX_3_0 = "SPDX_3_0";
export const SBOM_TYPE_SPDX_2_3 = "SPDX_2_3";

export const SBOM_NAME_SPDX_3_0 = "SPDX 3.0";
export const SBOM_NAME_SPDX_2_3 = "SPDX 2.3";
export const SBOM_NAME_CYCLONEDX_1_6 = "CycloneDX 1.6";
export const SBOM_NAME_EXCEL = "Excel";
export const SBOM_NAME_HTML_READABLE = "HTML (readable)";

export const SBOM_EXPORT_EXCEL = "excel";
/** Independent export type — not a serialization of SPDX/CycloneDX */
export const SBOM_EXPORT_HTML_READABLE = "html-readable";
export const SBOM_FORMAT_HTML = "HTML";

export const isSbomHtmlReadableExport = (typeOrValue) => {
  const value =
    typeof typeOrValue === "string" ? typeOrValue : typeOrValue?.value;
  return value === SBOM_EXPORT_HTML_READABLE;
};

/**
 * Excel KO/JA templates, export, and error copy.
 * Flip to true together with ExcelSbomI18n.I18N_ENABLED on the backend.
 */
export const EXCEL_I18N_ENABLED = false;

export const resolveExcelLanguage = (language = "en") =>
  EXCEL_I18N_ENABLED ? language || "en" : "en";

export const resolveSbomExportLanguage = (format, language = "en") =>
  String(format || "").toUpperCase() === "XLSX"
    ? resolveExcelLanguage(language)
    : language || "en";

export const SBOM_FORMAT_SPDX_3 = "spdx-3";
export const SBOM_FORMAT_SPDX_2_3 = "spdx-2.3";
export const SBOM_FORMAT_CYCLONEDX = "cyclonedx";

/** Values sent as multipart `sbomType` on POST /sbom_verification */
export const SBOM_VERIFICATION_TYPE_SPDX_3 = "spdx3";
export const SBOM_VERIFICATION_TYPE_SPDX_2_3 = "spdx2.3";
export const SBOM_VERIFICATION_TYPE_CYCLONEDX = "cyclonedx1.6";
export const SBOM_VERIFICATION_TYPE_EXCEL = "excel";
export const SBOM_VERIFICATION_TYPE_CLARITY_SC = "claritySc";

export const SBOM_MANUAL_FORMAT_OPTIONS = [
  { value: SBOM_VERIFICATION_TYPE_SPDX_2_3, label: SBOM_NAME_SPDX_2_3 },
  { value: SBOM_VERIFICATION_TYPE_SPDX_3, label: SBOM_NAME_SPDX_3_0 },
  { value: SBOM_VERIFICATION_TYPE_CYCLONEDX, label: SBOM_NAME_CYCLONEDX_1_6 },
  { value: SBOM_VERIFICATION_TYPE_EXCEL, label: SBOM_NAME_EXCEL },
  { value: SBOM_VERIFICATION_TYPE_CLARITY_SC, label: ClaritySC },
];

export const SBOM_DETECT_STATUS_OK = "OK";
export const SBOM_DETECT_STATUS_UNRECOGNIZED = "UNRECOGNIZED";
export const SBOM_DETECT_STATUS_UNSUPPORTED = "UNSUPPORTED_VERSION";

export const SBOM_IMPORT_EXTENSIONS_ALL = [
  ".json",
  ".txt",
  ".jsonld",
  ".xml",
  ".rdf",
  ".rdf.xml",
  ".xlsx",
];

export const SBOM_IMPORT_ACCEPT = SBOM_IMPORT_EXTENSIONS_ALL.filter((ext) => ext !== ".rdf.xml").join(",");

export const formatSbomImportExtensionsLabelAll = () => SBOM_IMPORT_EXTENSIONS_ALL.join(", ");

export const isAllowedSbomImportFileAny = (fileName) => {
  const ext = getSbomImportExtension(fileName);
  return SBOM_IMPORT_EXTENSIONS_ALL.includes(ext);
};

export const resolveSbomVerificationType = (sbom) => {
  if (!sbom) return null;
  if (sbom.sbomName === SBOM_NAME_SPDX_2_3) return SBOM_VERIFICATION_TYPE_SPDX_2_3;
  if (sbom.sbomName === SBOM_NAME_SPDX_3_0) return SBOM_VERIFICATION_TYPE_SPDX_3;
  if (sbom.sbomTypeCd === CYCLONEDX || sbom.sbomName === SBOM_NAME_CYCLONEDX_1_6) {
    return SBOM_VERIFICATION_TYPE_CYCLONEDX;
  }
  if (sbom.sbomTypeCd === Excel || sbom.sbomName === SBOM_NAME_EXCEL) {
    return SBOM_VERIFICATION_TYPE_EXCEL;
  }
  if (sbom.sbomTypeCd === ClaritySC) return SBOM_VERIFICATION_TYPE_CLARITY_SC;
  return null;
};

export const isClarityScSbom = (sbom) => sbom?.sbomTypeCd === ClaritySC;

export const resolveSbomFormat = (sbomData) => {
  if (!sbomData) return null;

  const { sbomName } = sbomData;

  if (sbomName === SBOM_NAME_SPDX_2_3) return SBOM_FORMAT_SPDX_2_3;
  if (sbomName === SBOM_NAME_SPDX_3_0) return SBOM_FORMAT_SPDX_3;
  if (sbomName === SBOM_NAME_CYCLONEDX_1_6) return SBOM_FORMAT_CYCLONEDX;

  return null;
};

const SBOM_EXPORT_FILE_LABELS = {
  [SBOM_NAME_CYCLONEDX_1_6]: "CycloneDX1.6",
  [SBOM_NAME_SPDX_2_3]: "SPDX2.3",
  [SBOM_NAME_SPDX_3_0]: "SPDX3.0",
  [SBOM_NAME_EXCEL]: "Excel",
};

export const getSbomExportFileLabel = (sbomName) => SBOM_EXPORT_FILE_LABELS[sbomName] ?? "";

export const getSbomImportExtension = (fileName) => {
  const name = (fileName || "").toLowerCase();
  if (name.endsWith(".rdf.xml")) return ".rdf.xml";
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot) : "";
};

export const getSbomImportExtensions = (sbom) => {
  if (!sbom) {
    return [...SBOM_IMPORT_EXTENSIONS_ALL];
  }
  if (sbom.sbomName === SBOM_NAME_SPDX_2_3) {
    return [".json", ".txt", ".xml", ".rdf", ".rdf.xml"];
  }
  if (sbom.sbomName === SBOM_NAME_SPDX_3_0) {
    return [".json", ".txt", ".jsonld"];
  }
  if (sbom.sbomTypeCd === CYCLONEDX || sbom.sbomName === SBOM_NAME_CYCLONEDX_1_6) {
    return [".json", ".txt", ".xml"];
  }
  if (sbom.sbomTypeCd === Excel) {
    return [".xlsx"];
  }
  return [".json", ".txt"];
};

export const getSbomImportAccept = (sbom) =>
  getSbomImportExtensions(sbom)
    .filter((ext) => ext !== ".rdf.xml")
    .join(",");

export const formatSbomImportExtensionsLabel = (sbom) =>
  getSbomImportExtensions(sbom).join(", ");

export const isAllowedSbomImportFile = (fileName, sbom) => {
  const ext = getSbomImportExtension(fileName);
  return getSbomImportExtensions(sbom).includes(ext);
};

export const isJsonSbomExportFormat = (format) => format === "JSON" || format === "CSAF";

export const getSbomExportResponseType = (format) => {
  if (format === "XLSX") return "arraybuffer";
  if (isJsonSbomExportFormat(format)) return "json";
  return "text";
};

const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const toTextContent = (data) => {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return new TextDecoder().decode(data);
  return String(data ?? "");
};

export const getSbomExportExtension = (format) => {
  if (format === "RDF/XML") return "rdf";
  if (format === "XML") return "xml";
  if (format === "XLSX") return "xlsx";
  return "json";
};

/**
 * Resolve an o2_sbom row for export download APIs that require sbomSeq.
 * Excel export is format-driven (XLSX) and does not require a dedicated "Excel" SBOM setting.
 */
export const resolveSbomSettingForExport = (sbomList = [], targetSbomName, format) => {
  const list = Array.isArray(sbomList) ? sbomList : [];
  const exact = list.find((sbom) => sbom.sbomName === targetSbomName);
  if (exact?.sbomSeq) {
    return exact;
  }
  if (format === "XLSX") {
    return (
      list.find((sbom) => sbom.sbomName === SYSTEM_DEFAULT_SBOM_NAME) ||
      list.find((sbom) => sbom.sbomTypeCd === ClaritySC) ||
      list.find((sbom) => sbom.sbomSeq) ||
      null
    );
  }
  return null;
};

export const buildSbomDownloadPayload = (data, format) => {
  const extension = getSbomExportExtension(format);
  if (format === "XLSX") {
    const bytes = data instanceof ArrayBuffer ? data : data instanceof Blob ? data : null;
    if (bytes instanceof Blob) {
      return { blob: bytes, extension };
    }
    return {
      blob: new Blob([bytes ?? data], { type: XLSX_MIME_TYPE }),
      extension,
    };
  }
  if (format === "XML" || format === "RDF/XML") {
    const content = toTextContent(data);
    return {
      blob: new Blob([content], {
        type: format === "RDF/XML" ? "application/rdf+xml" : "application/xml",
      }),
      extension,
    };
  }

  const parsed = typeof data === "string" || data instanceof ArrayBuffer
    ? JSON.parse(toTextContent(data))
    : data;
  return {
    blob: new Blob([JSON.stringify(parsed, null, 2)], { type: "application/json" }),
    extension,
  };
};

export const getSbomVersion = (sbomData) => {
  switch (resolveSbomFormat(sbomData)) {
    case SBOM_FORMAT_SPDX_2_3:
      return "2.3";
    case SBOM_FORMAT_SPDX_3:
      return "3.0.1";
    case SBOM_FORMAT_CYCLONEDX:
      return "1.6";
    default:
      return null;
  }
};

// Options list
export const SBOM_TYPE_OPTIONS = [
  { value: SPDX, label: SPDX },
  { value: CYCLONEDX, label: "CycloneDX" },
  { value: ClaritySC, label: ClaritySC },
  { value: Excel, label: Excel },
];

// allowed for creation (system types such as SPDX, CycloneDX, Excel are seeded only)
export const CREATABLE_SBOM_TYPES = [ClaritySC];

export const NON_EDITABLE_SBOM_TYPES = [SPDX, CYCLONEDX, Excel];

export const isNonEditableSbom = (row) =>
  row?.sbomName === SYSTEM_DEFAULT_SBOM_NAME
  || NON_EDITABLE_SBOM_TYPES.includes(row?.sbomTypeCd);

/** System / fixed SBOM settings first, then user-created ClaritySC entries by sbomSeq. */
export const compareSbomListOrder = (a, b) => {
  const rank = (row) => (isNonEditableSbom(row) ? 0 : 1);
  const diff = rank(a) - rank(b);
  if (diff !== 0) return diff;
  return (a?.sbomSeq ?? 0) - (b?.sbomSeq ?? 0);
};

// Helper
export const isCreatable = (type) => CREATABLE_SBOM_TYPES.includes(type);

export const getSbomTypeLabel = (value) => {
  const found = SBOM_TYPE_OPTIONS.find(opt => opt.value === value);
  return found ? found.label : value;
};

export const CREATABLE_SBOM_TYPE_OPTIONS = SBOM_TYPE_OPTIONS.filter(
  (opt) => CREATABLE_SBOM_TYPES.includes(opt.value)
);


// Cyclone DX structure
export const CYCLONEDX_STRUCTURE = [
  {
    label: "CyclonedxDocument*",
    icon: FileText,
    children: [
      { label: "bomFormat*" },
      { label: "specVersion*" },
      { label: "serialNumber?" },
      { label: "version?" },
      {
        label: "metadata*",
        children: [
          { label: "timestamp*" },
          { label: "authors [{name}]?" },
          { label: "tool []?" },
          { label: "manufacture?" },
          { label: "properties?" },
        ],
      },
    ],
  },
  {
    label: "components? [Component]",
    icon: Package,
    children: [
      {
        label: "Component*",
        children: [
          { label: "bom-ref*" },
          { label: "type*" },
          { label: "name*" },
          { label: "version?" },
          { 
            label: "licenses []?",
            children: [
              {label: "expression*"}
            ]
          },
          { label: "externalReferences []?"},
          { label: "properties []?" },
        ],
      },
    ],
  },
  {
    label: "dependencies? [Dependency]",
    icon: Link2,
    children: [
      {
        label: "Dependency",
        children: [
          { label: "ref*" },
          { label: "dependencies? [ref]" },
        ],
      },
    ],
  },
  {
    label: "vulnerabilities? [Vulnerability]",
    icon: Shield,
    children: [
      {
        label: "Vulnerability",
        children: [
          { label: "id*" },
          { label: "created?" },
          { label: "updated?" },
          { label: "affects [ref]*" },
          { label: "rattings []?"}
        ],
      },
    ],
  },
];

// SPDX structure
export const SPDX_STRUCTURE = [
  {
    label: "SpdxDocument*",
    icon: FileText,
    children: [
      { label: "spdxId*" },
      { label: "type*" },
      { label: "name?" },
      { label: "dataLicense?" },
      { label: "creationInfo*" },
      { label: "rootElement?" },
    ],
  },
  {
    label: "software_Package?",
    icon: Package,
    children: [
      { label: "spdxId*" },
      { label: "type*" },
      { label: "name*" },
      { label: "packageVersion?" },
      { label: "software_downloadLocation?" },
      { label: "software_copyrightText?"},
      { label: "externalIdentifier []?" },
      { label: "externalRef []?" },
      { label: "creationInfo*" },
    ],
  },
  {
    label: "Relationship?",
    icon: Link2,
    children: [
      { label: "spdxId*" },
      { label: "type*" },
      { label: "relationshipType*" },
      { label: "from*" },
      { label: "to []*" },
      { label: "creationInfo*" },
    ],
  },
  {
    label: "CreationInfo*",
    icon: Wrench,
    children: [
      { label: "@id*" },
      { label: "type*" },
      { label: "specVersion*" },
      { label: "created*" },
      { label: "createdBy*" },
    ],
  },
  {
    label: "Person*",
    icon: User,
    children: [
      { label: "spdxId*" },
      { label: "type*" },
      { label: "name*" },
      { label: "creationInfo?" },
    ],
  },
];

// SPDX 2.3 structure (flat document)
export const SPDX_23_STRUCTURE = [
  {
    label: "SpdxDocument*",
    icon: FileText,
    children: [
      { label: "spdxVersion*" },
      { label: "dataLicense*" },
      { label: "SPDXID*" },
      { label: "name*" },
      { label: "documentNamespace*" },
      { label: "creationInfo*" },
      {
        label: "annotations []?",
        children: [
          {
            label: "Annotation",
            children: [
              { label: "annotationDate*" },
              { label: "annotationType*" },
              { label: "annotator*" },
              { label: "comment*" },
            ],
          },
        ],
      },
    ],
  },
  {
    label: "packages? [Package]",
    icon: Package,
    children: [
      {
        label: "Package*",
        children: [
          { label: "SPDXID*" },
          { label: "name*" },
          { label: "versionInfo?" },
          { label: "downloadLocation*" },
          { label: "filesAnalyzed?" },
          { label: "licenseDeclared?" },
          { label: "licenseConcluded?" },
          { label: "copyrightText?" },
          {
            label: "externalRefs []?",
            children: [
              {
                label: "ExternalRef",
                children: [
                  { label: "referenceCategory*" },
                  { label: "referenceType*" },
                  { label: "referenceLocator*" },
                  { label: "comment?" },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    label: "relationships? [Relationship]",
    icon: Link2,
    children: [
      {
        label: "Relationship",
        children: [
          { label: "spdxElementId*" },
          { label: "relationshipType*" },
          { label: "relatedSpdxElement*" },
          { label: "comment?" },
        ],
      },
    ],
  },
  {
    label: "CreationInfo*",
    icon: Wrench,
    children: [
      { label: "created*" },
      { label: "creators*" },
      { label: "licenseListVersion?" },
    ],
  },
];

export const SBOM_OPTIONS = [
  { value: CYCLONEDX, label: SBOM_NAME_CYCLONEDX_1_6 },
  { value: SBOM_TYPE_SPDX_3_0, label: SBOM_NAME_SPDX_3_0 },
  { value: SBOM_TYPE_SPDX_2_3, label: SBOM_NAME_SPDX_2_3 },
];